import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth";

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: "/login",
      name: "login",
      component: () => import("@/views/auth/LoginView.vue"),
      meta: { public: true },
    },
    {
      path: "/register",
      name: "register",
      component: () => import("@/views/auth/RegisterView.vue"),
      meta: { public: true },
    },
    {
      path: "/verify-email",
      name: "verify-email",
      component: () => import("@/views/auth/VerifyEmailView.vue"),
      meta: { public: true },
    },
    {
      path: "/forgot-password",
      name: "forgot-password",
      component: () => import("@/views/auth/ForgotPasswordView.vue"),
      meta: { public: true },
    },
    {
      path: "/reset-password",
      name: "reset-password",
      component: () => import("@/views/auth/ResetPasswordView.vue"),
      meta: { public: true },
    },
    {
      path: "/pos",
      name: "pos",
      component: () => import("@/views/pos/PosView.vue"),
      meta: { requiresAuth: true, requiresSeller: true },
    },
    {
      path: "/",
      component: () => import("@/components/layout/AppShell.vue"),
      meta: { requiresAuth: true },
      children: [
        {
          path: "",
          name: "dashboard",
          component: () => import("@/views/dashboard/DashboardView.vue"),
        },
        {
          path: "/inventory",
          component: () => import("@/views/inventory/InventoryLayout.vue"),
          redirect: "/inventory/products",
          children: [
            {
              path: "products",
              name: "inventory-products",
              component: () => import("@/views/inventory/ProductsView.vue"),
            },
            {
              path: "stock",
              name: "inventory-stock",
              component: () => import("@/views/inventory/StockView.vue"),
            },
            {
              path: "movements",
              name: "inventory-movements",
              component: () => import("@/views/inventory/MovementsView.vue"),
            },
          ],
        },
        {
          path: "/import",
          component: () => import("@/views/import/ImportLayout.vue"),
          redirect: "/import/local",
          children: [
            {
              path: "local",
              name: "import-local",
              component: () => import("@/views/import/LocalImportView.vue"),
            },
            {
              path: "inventory",
              name: "import-inventory",
              component: () => import("@/views/import/InventoryImportView.vue"),
            },
            {
              path: "customers",
              name: "import-customers",
              component: () => import("@/views/import/CustomerImportView.vue"),
            },
          ],
        },
        {
          path: "/clients",
          component: () => import("@/views/clients/ClientsLayout.vue"),
          redirect: "/clients/list",
          children: [
            {
              path: "list",
              name: "clients-list",
              component: () => import("@/views/sales/CustomersView.vue"),
            },
            {
              path: "import",
              name: "clients-import",
              component: () => import("@/views/import/CustomerImportView.vue"),
            },
          ],
        },
        {
          path: "/sales",
          component: () => import("@/views/sales/SalesLayout.vue"),
          redirect: "/sales/dashboard",
          children: [
            {
              path: "dashboard",
              name: "sales-dashboard",
              component: () => import("@/views/sales/SalesDashboardView.vue"),
            },
            {
              path: "customers",
              name: "sales-customers",
              component: () => import("@/views/sales/CustomersView.vue"),
            },
            {
              path: "quotes",
              name: "sales-quotes",
              component: () => import("@/views/sales/QuotesView.vue"),
            },
            {
              path: "invoices",
              name: "sales-invoices",
              component: () => import("@/views/sales/InvoicesView.vue"),
            },
            {
              path: "payments",
              name: "sales-payments",
              component: () => import("@/views/sales/PaymentsView.vue"),
            },
          ],
        },
        {
          path: "/operations",
          component: () => import("@/views/operations/OperationsLayout.vue"),
          redirect: "/operations/suppliers",
          children: [
            {
              path: "suppliers",
              name: "operations-suppliers",
              component: () => import("@/views/operations/SuppliersView.vue"),
            },
            {
              path: "purchase-orders",
              name: "operations-purchase-orders",
              component: () => import("@/views/operations/PurchaseOrdersView.vue"),
            },
          ],
        },
        {
          path: "/taller",
          component: () => import("@/views/taller/TallerLayout.vue"),
          redirect: "/taller/calendar",
          children: [
            {
              path: "calendar",
              name: "taller-calendar",
              component: () => import("@/views/taller/CalendarView.vue"),
            },
            {
              path: "appointments",
              name: "taller-appointments",
              component: () => import("@/views/taller/AppointmentsView.vue"),
            },
            {
              path: "work-orders",
              name: "taller-work-orders",
              component: () => import("@/views/taller/WorkOrdersView.vue"),
            },
          ],
        },
        {
          path: "/reports",
          component: () => import("@/views/reports/ReportsLayout.vue"),
          redirect: "/reports/sales",
          children: [
            {
              path: "sales",
              name: "reports-sales",
              component: () => import("@/views/reports/SalesReportsView.vue"),
            },
            {
              path: "inventory",
              name: "reports-inventory",
              component: () => import("@/views/reports/InventoryReportsView.vue"),
            },
            {
              path: "customers",
              name: "reports-customers",
              component: () => import("@/views/reports/CustomerReportsView.vue"),
            },
          ],
        },
        {
          path: "/admin/users",
          name: "admin-users",
          component: () => import("@/views/admin/UsersView.vue"),
          meta: { requiresManager: true },
        },
        {
          path: "/admin/branches",
          name: "admin-branches",
          component: () => import("@/views/admin/BranchesView.vue"),
          meta: { requiresCompanyAdmin: true },
        },
        {
          path: "/admin/companies",
          name: "admin-companies",
          component: () => import("@/views/admin/CompaniesView.vue"),
          meta: { adminOnly: true },
        },
        {
          path: "/settings",
          name: "settings",
          component: () => import("@/views/settings/SettingsView.vue"),
        },
        {
          path: "/company",
          name: "company-settings",
          component: () => import("@/views/company/CompanySettingsView.vue"),
          meta: { requiresCompanyAdmin: true },
        },
        {
          path: "/profile",
          name: "profile",
          component: () => import("@/views/profile/ProfileView.vue"),
        },
      ],
    },
  ],
});

router.beforeEach((to, _from, next) => {
  const authStore = useAuthStore();

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({ name: "login", query: { redirect: to.fullPath } });
    return;
  }

  if (to.meta.public && authStore.isAuthenticated) {
    next({ name: "dashboard" });
    return;
  }

  if (to.meta.adminOnly && !authStore.isAdmin) {
    next({ name: "dashboard" });
    return;
  }

  if (to.meta.requiresCompanyAdmin && !authStore.canManageBranches) {
    next({ name: "dashboard" });
    return;
  }

  if (to.meta.requiresManager && !authStore.canManageUsers) {
    next({ name: "dashboard" });
    return;
  }

  if (to.meta.requiresSeller && !authStore.user?.role && authStore.user?.role !== "viewer") {
    const role = authStore.user?.role;
    const allowed = ["admin", "company_admin", "manager", "seller", "mechanic"];
    if (!allowed.includes(role || "")) {
      next({ name: "dashboard" });
      return;
    }
  }

  next();
});

export default router;
